import React, { useState, useContext } from "react"
import reactionLeft from "../util/reactionLeft"
import JamEditor from "./JamEditor"
import createNewThread from "../util/fetchApiHelpers/createNewThread"
import { EditorState, ContentState, convertToRaw } from "draft-js"
import DeviceDetector from "device-detector-js"
import type { Thread, Mention } from "../types"
import { ConfettiContext } from "./Confetti"
import { UserContext } from "./UserContext"

export interface JamCreateNewCommentProps {
	x: number
	y: number
	elementSelectorForNewThread: string
	specificElement: Element

	JAM_ID: string
	mentions: Mention[]

	onThreadChange: (thread: Thread) => void

	loading: boolean
	onLoadingChange: (loading: boolean) => void

	socket: SocketIOClient.Socket
}

export default function JamCreateNewComment(props: JamCreateNewCommentProps) {
	let [editorState, setEditorState] = useState(() => EditorState.createEmpty())
	const { celebrate } = useContext(ConfettiContext)
	const { user: loggedInUser, updateUser } = useContext(UserContext)

	function handleEditorChange(editorState: EditorState) {
		setEditorState(editorState)
	}

	async function handleMessageSend() {
		// start loading
		props.onLoadingChange(true)

		// get content
		let message = convertToRaw(editorState.getCurrentContent())

		// get system info
		let deviceDetector = new DeviceDetector()
		let userAgent = navigator.userAgent
		let device = deviceDetector.parse(userAgent)
		let screenWidth = window.innerWidth
		let screenHeight = window.innerHeight

		let newThread = await createNewThread(
			props.x,
			props.y,
			JSON.stringify(message),
			props.JAM_ID,
			device,
			screenWidth,
			screenHeight,
			props.elementSelectorForNewThread,
		)

		props.onThreadChange(newThread)

		//stop loading
		props.onLoadingChange(false)

		if (loggedInUser && loggedInUser.commentCount === 0) {
			celebrate()
		}

		if (loggedInUser) {
			updateUser({ commentCount: loggedInUser.commentCount + 1 })
		}

		props.socket.emit("threadUpdate", newThread)

		// reset editor
		setEditorState(
			EditorState.push(
				editorState,
				ContentState.createFromText(""),
				"remove-range",
			),
		)
	}
	return (
		<div
			style={{ top: `${props.y}px`, left: `${reactionLeft(props.x)}px` }}
			className="jam--editor"
		>
			<div className="jam--thread-editor-container">
				<div className="jam--thread-header-container">
					<div style={{ display: "flex" }}>
						{loggedInUser && (
							<React.Fragment>
								<img
									className="jam--thread-header-author-avatar"
									src={loggedInUser.avatar}
								/>
								<div className="jam--thread-header-author-name">
									{loggedInUser.name}
								</div>
							</React.Fragment>
						)}
						<style>
							{`
								.jam--thread-header-author-avatar {
									align-self: center;
									height: 30px;
									width: 30px;
									border-radius: 50%;
								}
								.jam--thread-header-author-name {
									align-self: center;
									color: #2a3632;
									font-size: 14px;
									margin-left: 15px;
									font-weight: 500;
								}
							`}
						</style>
					</div>
					<div
						onClick={handleMessageSend}
						className="jam--button jam--thread-editor-send-button"
					>
						{props.loading ? "Sending..." : "Send"}
						<img
							className="jam--tiny-berry"
							src="https://strawberryjam.nyc3.cdn.digitaloceanspaces.com/icon.png"
						/>
					</div>
					<style>
						{`
							.jam--thread-editor-container {
								align-content: center;
								display: flex;
								flex-direction: column;
								align-items: center;
								width: 100%;
								font-size: 14px;
								z-index: 1000000;
							}
							.jam--thread-header-container {
								display: flex;
								flex-direction: row;
								justify-content: space-between;
								align-items: center;
								width: 100%;
								font-size: 14px;
								box-sizing: border-box;
								padding: 1rem;
							}
							.jam--thread-header-container div::selection,
							.jam--thread-header-container img::selection {
								outline: none;
							}
							.jam--thread-editor-send-button {
								align-content: flex-end;
								padding: 10px 24px;
								border: 1px solid #3c81c7;
								box-sizing: border-box;
								border-radius: 124px;
								display: flex;
								align-items: center;
								justify-content: center;
								height: 40px;
								color: #2a3632;
								font-size: 14px;
								line-height: 20px;
								margin: 0;
								font-weight: 500;
								background: rgba(100, 175, 250, 0.15);
								color: #3c81c7;
							}

							.jam--thread-editor-send-button:hover {
								cursor: pointer;
							}

							.jam--tiny-berry {
								width: 20px;
								margin-left: 8px;
							}
						`}
					</style>
				</div>
				{/* below into JamEditor: styles={this.props.styles} */}
				<JamEditor
					onEditorChange={handleEditorChange}
					editorState={editorState}
					onMessageSend={handleMessageSend}
					mentions={props.mentions}
					specificElement={props.specificElement}
				/>
			</div>
			<style>
				{`
					.jam--editor {
						width: 365px;
						background: #fff;
						box-sizing: border-box;
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: space-around;
						border-radius: 10px;
						box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
						border: 1px solid #f7f7f7;
						font-family: "SF Pro Text",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
						Helvetica, Arial, sans-serif;
						position: absolute;
						font-size: 14px;
						z-index: 1000001;
						padding: 0.3rem;
					}

					@media screen and (max-width: 450px) {
						.jam--editor {
							margin-left: auto;
							margin-right: auto;
							left: 0 !important;
							right: 0;
						}
					}

					.jam--editor .jam--reaction-avatar {
						position: relative;
						top: -1px;
						width: 30px;
						height: 30px;
						bottom: 4px;
						border-radius: 50%;
					}
				`}
			</style>
		</div>
	)
}
